// frontend/src/pages/crm/FieldActivityPage.tsx
// AI-fc37 (2026-07-28) — Field-Activity pre-stage board (Option A), web.
//
// Field leads captured in person sit in a pre-pipeline holding phase, colour-
// coded by contact quality, until the decision-maker's number lands. They are
// deliberately absent from the normal Leads pipeline + sprint queue (the backend
// excludes red/yellow via mergeFieldActivityVisible). This board is where a
// manager/field lead works them: revisit → enter number + whose-number → the
// colour re-derives; GREEN auto-promotes the lead into "New Leads".
//
// Spec: docs/superpowers/specs/2026-07-28-field-activity-prestage-design.md

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/utils/api';

type FieldLead = {
  id: string;
  name: string | null;
  phone: string | null;
  company: string | null;
  location: string | null;
  product: string | null;
  isDecisionMaker: boolean | null;
  fieldActivityStatus: 'red' | 'yellow' | string | null;
  createdAt: string;
};

type Choice = 'dm' | 'gatekeeper' | 'noNumber';

const DOT: Record<string, string> = {
  red: '#D5433B',
  yellow: '#E0A02E',
  green: '#3AA06B',
};

export function FieldActivityPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<{ red: number; yellow: number }>({ red: 0, yellow: 0 });
  const [leads, setLeads] = useState<FieldLead[]>([]);
  const [editing, setEditing] = useState<FieldLead | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/field-activity');
      const data = res.data?.data ?? {};
      setCounts({ red: data.counts?.red ?? 0, yellow: data.counts?.yellow ?? 0 });
      setLeads(Array.isArray(data.leads) ? data.leads : []);
    } catch (e: any) {
      setError(e?.message || 'Could not load field activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Field Activity</h1>
        <Link to="/leads" style={{ fontSize: 14, color: '#6B7FD6' }}>← Back to Leads</Link>
      </div>
      <p style={{ color: '#6b7280', marginTop: 0, fontSize: 13 }}>
        Field leads still chasing a decision-maker's number. They are hidden from the pipeline
        and sprint queue until a DM number is captured — then they auto-promote into New Leads.
      </p>

      <div style={{ display: 'flex', gap: 10, margin: '12px 0 18px' }}>
        <CountChip label="No number" n={counts.red} color={DOT.red} />
        <CountChip label="Site contact" n={counts.yellow} color={DOT.yellow} />
      </div>

      {loading && <div>Loading…</div>}
      {error && <div style={{ color: '#b91c1c' }}>{error}</div>}
      {!loading && !error && leads.length === 0 && (
        <div style={{ color: '#6b7280', padding: '40px 0', textAlign: 'center' }}>
          No field-activity leads. New field leads without a decision-maker number will appear here.
        </div>
      )}

      {!loading && leads.map((lead) => (
        <div
          key={lead.id}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginBottom: 8,
          }}
        >
          <span style={{
            width: 12, height: 12, borderRadius: '50%',
            background: DOT[lead.fieldActivityStatus ?? ''] ?? '#9ca3af', flex: '0 0 auto',
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{lead.name || lead.company || 'Unnamed lead'}</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              {[
                lead.company && lead.company !== lead.name ? lead.company : null,
                lead.phone ? lead.phone : 'No number yet',
                lead.location || null,
              ].filter(Boolean).join(' · ')}
            </div>
          </div>
          <button
            onClick={() => setEditing(lead)}
            style={{
              border: '1px solid #6B7FD6', color: '#6B7FD6', background: 'transparent',
              borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13,
            }}
          >
            Revisit
          </button>
        </div>
      ))}

      {editing && (
        <RevisitModal
          lead={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function CountChip({ label, n, color }: { label: string; n: number; color: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      background: `${color}1f`, borderRadius: 10, padding: '6px 12px', fontWeight: 600, fontSize: 13,
    }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
      {label} {n}
    </span>
  );
}

function RevisitModal({
  lead, onClose, onSaved,
}: { lead: FieldLead; onClose: () => void; onSaved: () => void }) {
  const [digits, setDigits] = useState(
    (lead.phone ?? '').replace(/\D/g, '').replace(/^91/, ''),
  );
  const [choice, setChoice] = useState<Choice>(lead.isDecisionMaker ? 'dm' : 'gatekeeper');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    const wantsNumber = choice !== 'noNumber';
    const phone = wantsNumber && digits.trim() ? digits.trim() : null;
    const isDecisionMaker = choice === 'dm';
    try {
      const res = await api.patch(`/field-activity/${lead.id}`, { phone, isDecisionMaker });
      const promoted = res.data?.data?.promoted === true;
      // eslint-disable-next-line no-alert
      if (promoted) alert('Promoted to New Leads — the lead is now in the pipeline.');
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Could not save');
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 12, padding: 20, width: 380, maxWidth: '90vw' }}
      >
        <h3 style={{ marginTop: 0 }}>Revisit — {lead.name || 'lead'}</h3>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          Mobile number (leave blank if none)
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
          <span style={{ color: '#6b7280' }}>+91</span>
          <input
            value={digits}
            onChange={(e) => setDigits(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px' }}
          />
        </div>

        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Whose number is this?</div>
        {([
          ['dm', "Decision-maker's number"],
          ['gatekeeper', 'Someone else (guard / gatekeeper)'],
          ['noNumber', 'No number yet'],
        ] as [Choice, string][]).map(([val, txt]) => (
          <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
            <input type="radio" name="whose" checked={choice === val} onChange={() => setChoice(val)} />
            {txt}
          </label>
        ))}

        {err && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 6 }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#6B7FD6', color: '#fff', cursor: 'pointer' }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FieldActivityPage;
